import { useState, useCallback } from 'react'
import { db } from '@/services/index.ts'
import { useWallet } from './useWallet.ts'

export interface Profile {
  id: string
  wallet_address: string
  name: string | null
  email: string
  phone: string | null
  created_at: string
  updated_at: string
  notify_email?: boolean | null
  notify_sms?: boolean | null
  notify_push?: boolean | null
}

interface UseProfileState {
  profile: Profile | null
  loading: boolean
  error: string | null
}

interface UseProfileReturn extends UseProfileState {
  fetchProfile: () => Promise<Profile | null>
  saveProfile: (data: {
    email: string
    name?: string
    phone?: string
    notify_email?: boolean
    notify_sms?: boolean
    notify_push?: boolean
  }) => Promise<Profile | null>
  updateProfile: (
    data: Partial<
      Pick<
        Profile,
        | 'email'
        | 'name'
        | 'phone'
        | 'notify_email'
        | 'notify_sms'
        | 'notify_push'
      >
    >,
  ) => Promise<boolean>
  hasProfile: () => Promise<boolean>
  getProfileByWallet: (wallet: string) => Promise<Profile | null>
}

export function useProfile(): UseProfileReturn {
  const { address } = useWallet()

  const [state, setState] = useState<UseProfileState>({
    profile: null,
    loading: false,
    error: null,
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }))

  const fetchProfile = useCallback(async (): Promise<Profile | null> => {
    if (!address) return null
    setLoading()
    try {
      const { data, error } = await db.findOne<Profile>('profiles', {
        filters: [{ column: 'wallet_address', operator: 'eq', value: address }],
      })
      if (error) {
        setError(error.message)
        return null
      }
      setState((s) => ({ ...s, profile: data, loading: false }))
      return data
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch profile')
      return null
    }
  }, [address])

  const saveProfile = useCallback(
    async (data: {
      email: string
      name?: string
      phone?: string
      notify_email?: boolean
      notify_sms?: boolean
      notify_push?: boolean
    }): Promise<Profile | null> => {
      if (!address) return null
      setLoading()
      try {
        const { data: result, error } = await db.upsert<Profile>(
          'profiles',
          {
            wallet_address: address,
            email: data.email,
            name: data.name ?? null,
            phone: data.phone ?? null,
            updated_at: new Date().toISOString(),
            ...(data.notify_email !== undefined && {
              notify_email: data.notify_email,
            }),
            ...(data.notify_sms !== undefined && {
              notify_sms: data.notify_sms,
            }),
            ...(data.notify_push !== undefined && {
              notify_push: data.notify_push,
            }),
          },
          'wallet_address',
        )
        if (error) {
          setError(error.message)
          return null
        }
        const profile = result?.[0] ?? null
        setState((s) => ({ ...s, profile, loading: false }))
        return profile
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to save profile')
        return null
      }
    },
    [address],
  )

  const updateProfile = useCallback(
    async (
      data: Partial<
        Pick<
          Profile,
          | 'email'
          | 'name'
          | 'phone'
          | 'notify_email'
          | 'notify_sms'
          | 'notify_push'
        >
      >,
    ): Promise<boolean> => {
      if (!address) return false
      setLoading()
      try {
        const { error } = await db.update(
          'profiles',
          {
            ...data,
            updated_at: new Date().toISOString(),
          },
          [{ column: 'wallet_address', operator: 'eq', value: address }],
        )
        if (error) {
          setError(error.message)
          return false
        }
        setState((s) => ({
          ...s,
          profile: s.profile ? { ...s.profile, ...data } : null,
          loading: false,
        }))
        return true
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update profile')
        return false
      }
    },
    [address],
  )

  const hasProfile = useCallback(async (): Promise<boolean> => {
    if (!address) return false
    try {
      const { data } = await db.findOne<Profile>('profiles', {
        filters: [{ column: 'wallet_address', operator: 'eq', value: address }],
      })
      return data !== null
    } catch {
      return false
    }
  }, [address])

  const getProfileByWallet = useCallback(
    async (wallet: string): Promise<Profile | null> => {
      try {
        const normalized = wallet.toLowerCase()
        const { data } = await db.findOne<Profile>('profiles', {
          filters: [
            {
              column: 'wallet_address',
              operator: 'eq',
              value: normalized,
            } as const,
          ],
        })
        return data
      } catch {
        return null
      }
    },
    [],
  )

  return {
    ...state,
    fetchProfile,
    saveProfile,
    updateProfile,
    hasProfile,
    getProfileByWallet,
  }
}
